import { Box } from '@hivebrite/design-system/Box';
import { IconStrips } from '@hivebrite/design-system/Icon';

import BuilderThemeProvider, {
  VIEW_AS,
} from 'hb-builder/components/common/BuilderThemeProvider';
import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import { defaultTabs } from 'hb-builder/constants/tabs';
import {
  useComponentContext,
  useComponentsContext,
  useBuilderConfigContext,
  useRendererContext,
} from 'hb-builder/hooks';
import type { BuilderComponent } from 'hb-builder/types';
import { createNewItem } from 'hb-builder/utils/createNewItem';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { StripsItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/stripsItemAttributes';

import { INITIAL_STRIPS_IMAGES, INITIAL_STRIPS_SLIDER } from './constants';
import { getStripItemChildren } from './constants/childrenComponents';

const StripsTemplateWithButton: BuilderComponent<StripsItemAttributes> = () => (
  <div />
); // never used

const RenderBuilder = ({ children }) => {
  const {
    hivebrite: { networkFont },
  } = useBuilderConfigContext();
  const { item } = useComponentContext();
  const { components } = useComponentsContext();

  const { attributes } = item;
  const { Section } = components;

  return (
    <Section.RenderBuilder attributes={attributes}>
      <BuilderThemeProvider viewAs={VIEW_AS.USER}>
        <Box fontFamily={networkFont} width="100%">
          {children}
        </Box>
      </BuilderThemeProvider>
    </Section.RenderBuilder>
  );
};

const Render = ({
  children,
  item = { id: '', type: '' },
}: RenderProps<DocumentItem<StripsItemAttributes>>) => {
  const { bodyWidth, isMobilePreview, subdomainNetworkUrl, workspaceConfig } =
    useRendererContext();
  const { components } = useComponentsContext();
  const { Section } = components;

  return (
    <Section.Render
      bodyWidth={bodyWidth}
      isMobilePreview={isMobilePreview}
      item={item}
      subdomainNetworkUrl={subdomainNetworkUrl}
      workspaceConfig={workspaceConfig}
    >
      {children}
    </Section.Render>
  );
};

StripsTemplateWithButton.item = (props) => {
  const { bodyWidth, components, features } = props;
  const { Section, StripsSlider, TemplateWithButtonStripsItem } = components;

  const getInitialStripItem = (image) =>
    createNewItem(
      TemplateWithButtonStripsItem.item({
        ...props,
        children: getStripItemChildren({
          ...props,
          image,
        }),
        template: StripsTemplateWithButton.componentName,
      }),
    );

  const stripsSlider = createNewItem(
    StripsSlider.item({
      ...props,
      attributes: INITIAL_STRIPS_SLIDER,
      children: INITIAL_STRIPS_IMAGES({ features }).map((image) =>
        getInitialStripItem(image),
      ),
      template: StripsTemplateWithButton.componentName,
    }),
  );

  return Section.item({
    ...props,
    children: [stripsSlider],
    features: {
      ...features,
      section: {
        ...features?.section,
        fullWidth: true,
        containerBackgroundColor: 'white',
        containerPadding: '0 0 20px 0',
        keepContainerPaddingScreenS: true,
        name: StripsTemplateWithButton.componentName,
        sectionWidth: bodyWidth,
        type: StripsTemplateWithButton.componentName,
      },
    },
  });
};

StripsTemplateWithButton.componentCategory = 'block';
StripsTemplateWithButton.componentFamily = 'Strips';
StripsTemplateWithButton.componentIcon = () => IconStrips;
StripsTemplateWithButton.componentLabel = __('Strips');
StripsTemplateWithButton.componentName = 'StripsTemplateWithButton';
StripsTemplateWithButton.componentTemplateLabel = __('Strips With Button');
StripsTemplateWithButton.componentType = 'section';

StripsTemplateWithButton.canBeDoubleClicked = true;
StripsTemplateWithButton.canBeDragged = true;
StripsTemplateWithButton.canBeListed = true;
StripsTemplateWithButton.canBeSelected = true;
StripsTemplateWithButton.componentClickable = false;

StripsTemplateWithButton.toolbar = ['edit', 'drag', 'duplicate', 'delete'];

StripsTemplateWithButton.SettingsTabs = defaultTabs;

StripsTemplateWithButton.RenderBuilder = RenderBuilder;
StripsTemplateWithButton.Render = Render;

export default StripsTemplateWithButton;
